import {
  FUNNEL_BEFORE_FIRST_STAGE,
  type FunnelExample,
} from "@repo/app-types/api/funnel";
import { Color } from "@repo/app-types/constants/colors";
import {
  FunnelCohort,
  FunnelTurnRole,
  type FunnelId,
  type FunnelStages,
} from "@repo/app-types/entities/funnel";
import type { OutcomeGreenRedYellowResultCategory } from "@repo/app-types/entities/outcome";
import { RecordingSpeakerRoleType } from "@repo/app-types/entities/recording";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { buildCustomerInteractionUrl } from "@repo/app-types/helpers/customerInteractionUrl";
import { buttonStyle, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Link } from "@repo/ui/Link/routerLink";
import { Pill, type PillColor } from "@repo/ui/Pills/pill";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  SlideoverDialog,
  SlideoverDialogHeader,
  SlideoverDialogTitle,
} from "@repo/ui/Slideover/slideoverDialog";
import {
  IconChevronLeft,
  IconChevronRight,
  IconExternalLink,
} from "@tabler/icons-react";
import { useState, type FC } from "react";
import { Heading } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useFunnelExamplesQuery } from "../../../api/entities/funnel.js";
import { useGetTeammateAgentQuery } from "../../../api/entities/teammateAgent.js";
import { appPaths } from "../../../app/appPaths.js";
import alpharunLogoUrl from "../../../assets/copilot/alpharunLogo.png";
import { TranscriptContainer } from "../../../components/transcript/transcriptContainer.js";
import { TranscriptMessage } from "../../../components/transcript/transcriptMessage.js";
import {
  TranscriptSpeakerAvatar,
  type TranscriptSpeakerAvatarProps,
} from "../../../components/transcript/transcriptSpeakerAvatar.js";
import { useCopilotProps } from "../../copilot/copilotContext.js";
import {
  findFunnelOutcomeColor,
  funnelColorClassNames,
  formatFunnelOutcomeLabel,
  formatFunnelPreciseShare,
  formatFunnelShare,
  formatFunnelStageInteractions,
  formatFunnelStageName,
  type FunnelStageMomentRow,
  sortFunnelExitsByCategory,
} from "./funnelFormatting.js";
import { FunnelMomentShareBar } from "./funnelMomentShareBar.js";

const outcomePillColors: Record<
  OutcomeGreenRedYellowResultCategory,
  PillColor
> = {
  green: Color.Green,
  red: Color.Red,
  yellow: Color.Yellow,
  gray: Color.Gray,
};

export interface FunnelMomentAskButtonProps {
  momentName: string;
  stages: FunnelStages;
  /** Null is before the first stage. */
  stageKey: string | null;
  className?: string;
  onAsk?: () => void;
}

/** Drafts a copilot prompt to improve the agent on this moment; nothing without a copilot. */
export const FunnelMomentAskButton: FC<FunnelMomentAskButtonProps> = (
  props,
) => {
  const { momentName, stages, stageKey, className, onAsk } = props;
  const copilot = useCopilotProps();
  if (!copilot) {
    return null;
  }

  const stagePhrase =
    stageKey === null
      ? `before the ${stages[0]?.name ?? "first"} stage`
      : `in the ${formatFunnelStageName(stages, stageKey)} stage`;

  return (
    <IconButton
      variant={ButtonVariant.Tertiary}
      size={Size.SMALL}
      accessibleLabel={`Ask Alpharun to improve: ${momentName}`}
      className={className}
      icon={
        <span className="flex size-5 items-center justify-center rounded-md bg-foreground p-1">
          <img src={alpharunLogoUrl} alt="" className="size-full" />
        </span>
      }
      onClick={() => {
        const prompt = `Help me optimize this voice agent’s handling of “${momentName}” ${stagePhrase}. Review its current instructions, compare relevant agent exchanges with top-rep examples, and propose specific instruction changes. Evaluate the changes against the agent’s selling strategy if available; otherwise, ask me for the strategy before making assumptions. Explain how the changes could improve conversion and check other moments for regressions.`;
        copilot.onComposerDraftChange(
          copilot.composerDraft.trim()
            ? `${copilot.composerDraft}\n\n${prompt}`
            : prompt,
        );
        copilot.onOpenChat();
        onAsk?.();
      }}
    />
  );
};

export interface FunnelMomentSlideoverProps {
  funnelId: FunnelId;
  teammateAgentId: TeammateId;
  stages: FunnelStages;
  /** Null is before the first stage. */
  stageKey: string | null;
  row: FunnelStageMomentRow;
  /** The top reps' share of the stage with this moment; null without any. */
  topRepShare: number | null;
  /** Called once the copilot has the prompt, to close the slideover. */
  onAsk: () => void;
}

export const FunnelMomentSlideover: FC<FunnelMomentSlideoverProps> = (
  props,
) => {
  const {
    funnelId,
    teammateAgentId,
    stages,
    stageKey,
    row,
    topRepShare,
    onAsk,
  } = props;
  const { data: teammateAgent } = useGetTeammateAgentQuery(teammateAgentId);

  const {
    data: examples,
    isFetching,
    isError,
  } = useFunnelExamplesQuery(
    {
      funnelId,
      momentKey: row.momentKey,
      stageKey: stageKey ?? FUNNEL_BEFORE_FIRST_STAGE,
      voiceAgentTeammateId: teammateAgentId,
    },
    // Reopening a moment shows the exchanges labeled since.
    { refetchOnMountOrArgChange: true },
  );

  return (
    <SlideoverDialog label={row.momentName}>
      <SlideoverDialogHeader>
        <div className="flex min-w-0 items-center gap-2">
          <SlideoverDialogTitle>{row.momentName}</SlideoverDialogTitle>
          <FunnelMomentAskButton
            momentName={row.momentName}
            stages={stages}
            stageKey={stageKey}
            onAsk={onAsk}
          />
        </div>
      </SlideoverDialogHeader>
      <div className="flex min-h-0 flex-1 flex-col">
        {row.momentDescription ? (
          <p className="mx-6 mt-5 shrink-0 text-sm text-muted-foreground">
            {row.momentDescription}
          </p>
        ) : null}
        <FunnelMomentMetrics
          stages={stages}
          stageKey={stageKey}
          row={row}
          topRepShare={topRepShare}
        />
        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto border-t border-border-light md:grid-cols-2 md:grid-rows-1 md:divide-x md:divide-border-light md:overflow-y-visible">
          {[FunnelCohort.Agent, FunnelCohort.TopRep].map((cohort) => (
            <FunnelExamplePane
              key={cohort}
              cohort={cohort}
              examples={examples?.[cohort]}
              isFetching={isFetching}
              isError={isError}
              teammateAgentId={teammateAgentId}
              agentName={teammateAgent?.name}
            />
          ))}
        </div>
      </div>
    </SlideoverDialog>
  );
};

interface FunnelExamplePaneProps {
  cohort: FunnelCohort;
  /** Undefined until the examples load. */
  examples: readonly FunnelExample[] | undefined;
  isFetching: boolean;
  isError: boolean;
  teammateAgentId: TeammateId;
  /** Undefined until the agent loads. */
  agentName: string | undefined;
}

/** One cohort's exchanges, one at a time. */
const FunnelExamplePane: FC<FunnelExamplePaneProps> = (props) => {
  const { cohort, examples, isFetching, isError, teammateAgentId, agentName } =
    props;
  const [exampleIndex, setExampleIndex] = useState(0);
  const example = examples?.[exampleIndex];
  const isAgent = cohort === FunnelCohort.Agent;
  const name = isAgent
    ? (agentName ?? "Agent")
    : (example?.teammate_name ?? "Top reps");

  return (
    <section
      className="min-w-0 space-y-5 p-6 md:min-h-0 md:overflow-y-auto"
      aria-label={isAgent ? "Agent exchanges" : "Top-rep exchanges"}
    >
      <div className="flex min-h-10 items-center gap-3">
        <TranscriptSpeakerAvatar
          speakerName={name}
          speakerRole={RecordingSpeakerRoleType.Representative}
          isAgent={isAgent}
        />
        <h2 className="min-w-0 text-base font-semibold">{name}</h2>
      </div>
      {isFetching ? (
        <DefaultSkeleton />
      ) : isError || !examples ? (
        <p className="text-sm text-red-700">
          We couldn&apos;t load the examples. Refresh to try again.
        </p>
      ) : !example ? (
        <p className="text-sm text-muted-foreground">No examples yet</p>
      ) : (
        <>
          <FunnelExampleNavigation
            index={exampleIndex}
            total={examples.length}
            onIndexChange={setExampleIndex}
            interactionHref={
              isAgent
                ? appPaths.teammateAgentCall.build({
                    teammateAgentId,
                    gradingId: example.grading_id,
                  })
                : buildCustomerInteractionUrl({
                    playbookId: example.playbook_id,
                    gradingId: example.grading_id,
                  })
            }
          />
          <div>
            {example.continued ? (
              <Pill size={Size.SMALL} label="Continued" color={Color.Blue} />
            ) : (
              <Pill
                size={Size.SMALL}
                label={formatFunnelOutcomeLabel(example.outcome_label)}
                color={
                  example.outcome_category
                    ? outcomePillColors[example.outcome_category]
                    : Color.Gray
                }
              />
            )}
          </div>
          <p className="text-sm leading-relaxed text-foreground">
            {example.commentary}
          </p>
          <FunnelExampleTranscript
            example={example}
            agentName={agentName ?? "Agent"}
          />
        </>
      )}
    </section>
  );
};

interface FunnelMomentMetricsProps {
  stages: FunnelStages;
  stageKey: string | null;
  row: FunnelStageMomentRow;
  topRepShare: number | null;
}

const FunnelMomentMetrics: FC<FunnelMomentMetricsProps> = (props) => {
  const { stages, stageKey, row, topRepShare } = props;
  const { share, dropOffRate } = row;
  const stageInteractions = formatFunnelStageInteractions(stages, stageKey);
  const droppedOffShare = share * (dropOffRate ?? 0);

  return (
    <section
      className="mx-6 mt-5 mb-5 shrink-0 space-y-3 rounded-lg border border-border-light bg-card p-4"
      aria-label="Moment metrics"
    >
      <div className="flex items-end justify-between gap-4">
        <p className="text-sm font-medium text-foreground">
          Share of {stageInteractions} with this moment
        </p>
        <p className="text-xl font-semibold tabular-nums">
          {formatFunnelShare(share)}
        </p>
      </div>
      {dropOffRate !== null && (
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {formatFunnelShare(dropOffRate)}
          </span>{" "}
          of interactions with this moment dropped off
        </p>
      )}
      <FunnelMomentShareBar
        momentName={row.momentName}
        share={share}
        dropOffRate={dropOffRate}
        droppedOffByOutcome={row.droppedOffByOutcome}
        stageInteractions={stageInteractions}
        isExpanded
      />
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span
            className={twMerge(
              "size-2 rounded-full",
              funnelColorClassNames.continued.background,
            )}
          />
          {dropOffRate === null ? "Had this moment" : "Continued"}{" "}
          <span className="font-medium text-foreground">
            {formatFunnelPreciseShare(share - droppedOffShare)}
          </span>
        </span>
        {dropOffRate !== null &&
          sortFunnelExitsByCategory(row.droppedOffByOutcome).map((exit) => (
            <span
              key={`${exit.category ?? ""}:${exit.label ?? ""}`}
              className="flex items-center gap-1.5"
            >
              <span
                className={twMerge(
                  "size-2 rounded-full",
                  funnelColorClassNames[findFunnelOutcomeColor(exit.category)]
                    .background,
                )}
              />
              {formatFunnelOutcomeLabel(exit.label)}{" "}
              <span className="font-medium text-foreground">
                {formatFunnelPreciseShare(
                  row.droppedOff > 0
                    ? (droppedOffShare * exit.interactions) / row.droppedOff
                    : 0,
                )}
              </span>
            </span>
          ))}
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-border-strong/40" />
          No moment{" "}
          <span className="font-medium text-foreground">
            {formatFunnelPreciseShare(1 - share)}
          </span>
        </span>
      </div>
      {topRepShare !== null && (
        <p className="text-xs text-muted-foreground">
          Top reps: {formatFunnelShare(topRepShare)} of their{" "}
          {stageInteractions}
        </p>
      )}
    </section>
  );
};

interface FunnelExampleNavigationProps {
  index: number;
  total: number;
  onIndexChange: (index: number) => void;
  interactionHref: string;
}

const FunnelExampleNavigation: FC<FunnelExampleNavigationProps> = (props) => {
  const { index, total, onIndexChange, interactionHref } = props;

  return (
    <div className="flex items-center justify-between gap-3">
      <Heading level={2} className="text-sm font-semibold">
        Exchange {index + 1} of {total}
      </Heading>
      <div className="flex items-center gap-2">
        <Link
          href={interactionHref}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonStyle({
            variant: ButtonVariant.Tertiary,
            size: Size.SMALL,
          })}
        >
          <span>Go to interaction</span>
          <IconExternalLink className="size-4" />
        </Link>
        <div className="flex" role="group" aria-label="Exchange navigation">
          <IconButton
            variant={ButtonVariant.Tertiary}
            size={Size.SMALL}
            className="rounded-r-none focus-visible:z-10 disabled:bg-transparent"
            accessibleLabel="Previous exchange"
            icon={<IconChevronLeft className="size-4" />}
            isDisabled={index <= 0}
            onClick={() => onIndexChange(index - 1)}
          />
          <IconButton
            variant={ButtonVariant.Tertiary}
            size={Size.SMALL}
            className="rounded-l-none focus-visible:z-10 disabled:bg-transparent"
            accessibleLabel="Next exchange"
            icon={<IconChevronRight className="size-4" />}
            isDisabled={index >= total - 1}
            onClick={() => onIndexChange(index + 1)}
          />
        </div>
      </div>
    </div>
  );
};

function buildSpeakerProps(
  role: FunnelTurnRole,
  agentName: string,
  repName: string,
): TranscriptSpeakerAvatarProps | null {
  switch (role) {
    case FunnelTurnRole.Caller:
      return {
        speakerName: "Caller",
        speakerRole: RecordingSpeakerRoleType.Customer,
      };
    case FunnelTurnRole.Agent:
      return {
        speakerName: agentName,
        speakerRole: RecordingSpeakerRoleType.Representative,
        isAgent: true,
      };
    case FunnelTurnRole.Rep:
      return {
        speakerName: repName,
        speakerRole: RecordingSpeakerRoleType.Representative,
        isAgent: false,
      };
    // Tool turns are the model's markup of a tool call, not something to read.
    case FunnelTurnRole.Tool:
      return null;
  }
}

interface FunnelExampleTranscriptProps {
  example: FunnelExample;
  agentName: string;
}

/** The exchange's turns. */
const FunnelExampleTranscript: FC<FunnelExampleTranscriptProps> = (props) => {
  const { example, agentName } = props;

  return (
    <TranscriptContainer>
      {example.snapshot.map((turn, index) => {
        const speakerProps = buildSpeakerProps(
          turn.role,
          agentName,
          example.teammate_name,
        );
        return speakerProps ? (
          <TranscriptMessage key={index} {...speakerProps}>
            {turn.text}
          </TranscriptMessage>
        ) : null;
      })}
    </TranscriptContainer>
  );
};
